import { useCallback, useEffect, useMemo, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSpinner,
  faRotate,
  faChartSimple,
  faChevronDown,
  faChevronRight,
  faWandMagicSparkles,
} from "@awesome.me/kit-70c1f0055f/icons/duotone/solid";
import clsx from "clsx";
import { Button } from "@/components/ui/button/Button";
import { CC_GHOST_BTN, CC_PANEL, CC_PANEL_FLUSH, CC_TABLE_HEAD } from "@/components/case-checking/caseCheckingChrome";
import { InsightsFilterBar } from "@/components/case-checking/InsightsFilterBar";
import { Switch } from "@/components/ui/switch/Switch";
import { SegmentedPillToggle } from "@/components/ui/segmented-tabs/SegmentedPillToggle";
import type { InsightsCaseRef } from "@/components/case-checking/PortfolioInsightsView";
import {
  concentrationLabel,
  fallbackStatementInsight,
  formatRatePct,
  sortProblemStatementRows,
  type StatementSortMode,
} from "@/components/case-checking/progenyPortfolioInsightsUi";
import {
  getPortfolioProblemStatementInsights,
  getPortfolioProblemStatements,
  type PortfolioProblemStatementInsights,
  type PortfolioProblemStatementRow,
  type PortfolioProblemStatements,
  type PortfolioRag,
  type ProblemStatementKeyRisk,
} from "@/services/api/case-checking";

const RAG_CELL: Record<PortfolioRag, string> = {
  red: "bg-red-100 text-red-800 ring-red-200 dark:bg-red-500/20 dark:text-red-300 dark:ring-red-500/30",
  amber: "bg-amber-100 text-amber-800 ring-amber-200 dark:bg-amber-500/15 dark:text-amber-300 dark:ring-amber-500/25",
  green: "bg-emerald-100 text-emerald-800 ring-emerald-200 dark:bg-emerald-500/15 dark:text-emerald-300 dark:ring-emerald-500/25",
  grey: "bg-slate-100 dark:bg-white/5 text-slate-500 ring-1 ring-slate-200 dark:ring-white/10",
};

const RATING_LABEL: Record<string, string> = {
  pass: "Yes",
  fail: "No",
  unclear: "Unclear",
  na: "N/A",
};

const SEVERITY_DOT: Record<ProblemStatementKeyRisk["severity"], string> = {
  critical: "bg-red-400",
  warning: "bg-amber-400",
  info: "bg-sky-400",
};

const SORT_OPTIONS: { id: StatementSortMode; label: string }[] = [
  { id: "catalog", label: "Catalog order" },
  { id: "fail_rate", label: "By fail %" },
  { id: "issue_rate", label: "By issue %" },
];

function relativeTime(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const mins = Math.round((Date.now() - then) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.round(hrs / 24)}d ago`;
}

/** Progeny-only portfolio insights driven by problem-statement ratings. */
export function ProgenyPortfolioInsightsView({ cases }: { cases: InsightsCaseRef[] }) {
  const [adviserFilter, setAdviserFilter] = useState("");
  const [caseTypeFilter, setCaseTypeFilter] = useState("");
  const [data, setData] = useState<PortfolioProblemStatements | null>(null);
  const [insights, setInsights] = useState<PortfolioProblemStatementInsights | null>(null);
  const [loadingData, setLoadingData] = useState(false);
  const [loadingInsights, setLoadingInsights] = useState(false);
  const [showGrid, setShowGrid] = useState(false);
  const [onlyIssues, setOnlyIssues] = useState(false);
  const [sortMode, setSortMode] = useState<StatementSortMode>("catalog");
  const [error, setError] = useState<string | null>(null);

  const adviserOptions = useMemo(() => {
    const names = new Set<string>();
    for (const c of cases) {
      const name = c.adviser.trim();
      if (name) names.add(name);
    }
    return Array.from(names).sort((a, b) => a.localeCompare(b));
  }, [cases]);

  const caseTypeOptions = useMemo(() => {
    const types = new Set<string>();
    for (const c of cases) if (c.caseType) types.add(c.caseType);
    return Array.from(types).sort();
  }, [cases]);

  const filteredCases = useMemo(() => {
    return cases.filter((c) => {
      if (adviserFilter && c.adviser !== adviserFilter) return false;
      if (caseTypeFilter && c.caseType !== caseTypeFilter) return false;
      return true;
    });
  }, [cases, adviserFilter, caseTypeFilter]);

  const hasActiveFilter = Boolean(adviserFilter || caseTypeFilter);
  const caseIds = useMemo((): string[] | undefined => {
    if (!hasActiveFilter) return undefined;
    return filteredCases.map((c) => c.id);
  }, [filteredCases, hasActiveFilter]);
  const caseIdsKey = caseIds?.join(",") ?? "all";

  const loadData = useCallback(async () => {
    if (hasActiveFilter && filteredCases.length === 0) {
      setData(null);
      return;
    }
    setLoadingData(true);
    setError(null);
    try {
      setData(await getPortfolioProblemStatements(caseIds));
    } catch (err) {
      console.error("Failed to load problem-statement insights:", err);
      setError("Could not load coverage insights.");
    } finally {
      setLoadingData(false);
    }
  }, [caseIds, filteredCases.length, hasActiveFilter]);

  const loadInsights = useCallback(
    async (refresh: boolean) => {
      if (hasActiveFilter && filteredCases.length === 0) {
        setInsights(null);
        return;
      }
      setLoadingInsights(true);
      try {
        setInsights(await getPortfolioProblemStatementInsights(caseIds, refresh));
      } catch (err) {
        console.error("Failed to load coverage digest:", err);
      } finally {
        setLoadingInsights(false);
      }
    },
    [caseIds, filteredCases.length, hasActiveFilter],
  );

  const refreshAll = useCallback(() => {
    void loadData();
    void loadInsights(true);
  }, [loadData, loadInsights]);

  useEffect(() => {
    void loadData();
    void loadInsights(false);
  }, [caseIdsKey, loadData, loadInsights]);

  const totals = data?.totals;

  const scorecardRows = useMemo(() => {
    if (!data) return [];
    const base = onlyIssues ? data.rows.filter((r) => r.fail_count + r.unclear_count > 0) : data.rows;
    return sortProblemStatementRows(base, sortMode);
  }, [data, onlyIssues, sortMode]);

  const visibleGridRows = useMemo(() => {
    if (!data) return [];
    return onlyIssues ? data.rows.filter((r) => r.fail_count + r.unclear_count > 0) : data.rows;
  }, [data, onlyIssues]);

  const reviewProgress = useMemo(() => {
    if (!data) return null;
    const withRuns = data.columns.filter((c) => c.has_run);
    const fullyReviewed = withRuns.filter(
      (c) => c.scorable_count > 0 && c.reviewed_count >= c.scorable_count,
    ).length;
    return { withRuns: withRuns.length, fullyReviewed };
  }, [data]);

  if (cases.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-slate-400">
        <FontAwesomeIcon icon={faChartSimple} className="mb-3 h-8 w-8 opacity-50" />
        <p className="text-sm">No cases yet. Insights appear once coverage review runs complete.</p>
      </div>
    );
  }

  if (hasActiveFilter && filteredCases.length === 0) {
    return (
      <div className="space-y-5">
        <Header summary="No cases match filters" onRefresh={() => {}} refreshing={false} />
        <Filters
          adviserFilter={adviserFilter}
          caseTypeFilter={caseTypeFilter}
          adviserOptions={adviserOptions}
          caseTypeOptions={caseTypeOptions}
          onAdviserChange={setAdviserFilter}
          onCaseTypeChange={setCaseTypeFilter}
          onClear={() => {
            setAdviserFilter("");
            setCaseTypeFilter("");
          }}
          hasActiveFilter={hasActiveFilter}
        />
        <p className="py-16 text-center text-sm text-slate-500">No cases match these filters.</p>
      </div>
    );
  }

  const refreshing = loadingData || loadingInsights;

  return (
    <div className="space-y-5" data-testid="progeny-portfolio-insights">
      <Header
        summary={
          loadingData && !data
            ? "Loading…"
            : `${totals?.cases_with_runs ?? 0} of ${totals?.cases ?? cases.length} cases with coverage review`
        }
        onRefresh={refreshAll}
        refreshing={refreshing}
      />

      <Filters
        adviserFilter={adviserFilter}
        caseTypeFilter={caseTypeFilter}
        adviserOptions={adviserOptions}
        caseTypeOptions={caseTypeOptions}
        onAdviserChange={setAdviserFilter}
        onCaseTypeChange={setCaseTypeFilter}
        onClear={() => {
          setAdviserFilter("");
          setCaseTypeFilter("");
        }}
        hasActiveFilter={hasActiveFilter}
      />

      {error ? (
        <div className="rounded-lg bg-red-500/10 px-4 py-3 text-sm text-red-300 ring-1 ring-red-500/20">{error}</div>
      ) : null}

      {refreshing && data ? (
        <div className={clsx(CC_PANEL, "flex items-center gap-2 py-2.5 text-sm text-slate-600 dark:text-slate-300")}>
          <FontAwesomeIcon icon={faSpinner} className="h-4 w-4 shrink-0 animate-spin text-indigo-400" />
          Refreshing coverage insights…
        </div>
      ) : null}

      {loadingData && !data ? (
        <p className="flex items-center gap-2 py-8 text-sm text-slate-400">
          <FontAwesomeIcon icon={faSpinner} className="h-4 w-4 animate-spin" />
          Loading coverage insights…
        </p>
      ) : data ? (
        <div className={clsx("space-y-5 transition-opacity", refreshing && "pointer-events-none opacity-50")}>
          {data.cases_missing_run.length > 0 ? (
            <div className="rounded-lg bg-amber-100 px-4 py-3 text-sm text-amber-800 dark:bg-amber-500/10 dark:text-amber-200 ring-1 ring-amber-300 dark:ring-amber-500/20">
              <span className="font-medium">{data.cases_missing_run.length} case(s)</span> still awaiting coverage
              review: {data.cases_missing_run.join(", ")}
            </div>
          ) : null}

          <KeyRisksPanel insights={insights} loading={loadingInsights && !insights} />

          <div className="grid grid-cols-2 gap-2 lg:grid-cols-5">
            <StatChip label="Cases reviewed" value={totals?.cases_with_runs ?? 0} />
            <StatChip label="Pass rate" value={formatRatePct(totals?.overall_pass_rate_pct ?? 0)} />
            <StatChip label="Yes" value={totals?.passed ?? 0} tone="emerald" />
            <StatChip label="Unclear" value={totals?.unclear ?? 0} tone="amber" />
            <StatChip label="No" value={totals?.failed ?? 0} tone="red" />
          </div>

          {reviewProgress && reviewProgress.withRuns > 0 ? (
            <div className={CC_PANEL}>
              <p className="text-sm text-slate-600 dark:text-slate-300">
                <span className="font-medium text-slate-900 dark:text-slate-100">{reviewProgress.fullyReviewed}</span> of{" "}
                <span className="font-medium text-slate-900 dark:text-slate-100">{reviewProgress.withRuns}</span> cases fully
                peer-reviewed
                {totals?.human_overrides ? (
                  <span className="text-slate-500"> · {totals.human_overrides} overrides across statements</span>
                ) : null}
              </p>
            </div>
          ) : null}

          <div className={CC_PANEL}>
            <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
              <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">Problem statement scorecard</h3>
              <div className="flex flex-wrap items-center gap-2">
                <div className="flex items-center gap-2">
                  <span className="text-xs text-slate-400">Issues only</span>
                  <Switch checked={onlyIssues} onChange={setOnlyIssues}>
                    <span className="sr-only">Issues only</span>
                  </Switch>
                </div>
                <SegmentedPillToggle
                  options={SORT_OPTIONS}
                  value={sortMode}
                  onChange={setSortMode}
                  aria-label="Sort scorecard"
                  optionTestIdPrefix="progeny-scorecard-sort"
                />
              </div>
            </div>

            {scorecardRows.length > 0 ? (
              <ul className="space-y-1">
                {scorecardRows.map((row) => (
                  <StatementScorecardRow
                    key={row.problem_statement_id}
                    row={row}
                    insightText={
                      insights?.statement_insights[row.problem_statement_id] ??
                      fallbackStatementInsight(row)
                    }
                  />
                ))}
              </ul>
            ) : (
              <p className="text-sm text-slate-500">No problem statements match this view.</p>
            )}
          </div>

          {insights?.priorities && insights.priorities.length > 0 ? (
            <div className={CC_PANEL}>
              <h3 className="mb-2 text-sm font-semibold text-slate-900 dark:text-slate-100">Suggested priorities</h3>
              <ul className="list-inside list-disc space-y-1 text-sm text-slate-600 dark:text-slate-300">
                {insights.priorities.map((p) => (
                  <li key={p}>{p}</li>
                ))}
              </ul>
            </div>
          ) : null}

          <div className={CC_PANEL_FLUSH}>
            <Button
              plain
              type="button"
              onClick={() => setShowGrid((v) => !v)}
              className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left transition-colors hover:bg-slate-50 dark:hover:bg-white/[0.02]"
            >
              <span className="text-sm font-medium text-slate-700 dark:text-slate-200">Full statement grid</span>
              <FontAwesomeIcon
                icon={showGrid ? faChevronDown : faChevronRight}
                className="h-3.5 w-3.5 text-slate-500"
              />
            </Button>

            {showGrid ? (
              <div className="border-t border-slate-200 dark:border-white/5 px-4 pb-4">
                <p className="py-3 text-xs text-slate-500">Problem statement × case</p>
                <div className="max-h-[50vh] overflow-auto rounded-lg ring-1 ring-slate-200 dark:ring-white/5">
                  <table className="w-full border-separate border-spacing-0 text-xs">
                    <thead className="sticky top-0 z-10">
                      <tr>
                        <th className={clsx("sticky left-0 z-20 min-w-[200px] border-b border-slate-200 dark:border-white/5 px-3 py-2 text-left font-medium text-slate-400", CC_TABLE_HEAD)}>
                          Statement
                        </th>
                        {data.columns.map((col) => (
                          <th
                            key={col.case_id}
                            className={clsx("whitespace-nowrap border-b border-slate-200 dark:border-white/5 px-2 py-2 font-medium text-slate-400", CC_TABLE_HEAD)}
                            title={col.client_name}
                          >
                            {col.client_name.length > 10
                              ? `${col.client_name.slice(0, 9)}…`
                              : col.client_name}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {visibleGridRows.map((row) => (
                        <tr key={row.problem_statement_id} className="hover:bg-slate-50 dark:hover:bg-white/[0.02]">
                          <td className="sticky left-0 z-10 border-b border-slate-200 dark:border-white/5 bg-white dark:bg-slate-900/90 px-3 py-1.5">
                            <span className="text-slate-700 dark:text-slate-200">{row.title}</span>
                            <span className="block text-[10px] text-slate-500">{row.problem_statement_id}</span>
                          </td>
                          {row.cells.map((cell) => (
                            <td key={cell.case_id} className="border-b border-slate-200 dark:border-white/5 px-1 py-1 text-center">
                              <span
                                className={clsx(
                                  "inline-block min-w-[40px] rounded px-1 py-0.5 text-[10px]",
                                  cell.rag ? RAG_CELL[cell.rag as PortfolioRag] : "text-slate-600",
                                )}
                                title={
                                  cell.human_reviewed && cell.agent_rating !== cell.rating
                                    ? `Agent: ${RATING_LABEL[cell.agent_rating ?? ""] ?? "—"} → Review: ${RATING_LABEL[cell.rating ?? ""] ?? "—"}`
                                    : undefined
                                }
                              >
                                {cell.rating ? (RATING_LABEL[cell.rating] ?? cell.rating) : "—"}
                              </span>
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function KeyRisksPanel({
  insights,
  loading,
}: {
  insights: PortfolioProblemStatementInsights | null;
  loading: boolean;
}) {
  return (
    <div className={CC_PANEL}>
      <div className="mb-3 flex items-center gap-2">
        <FontAwesomeIcon icon={faWandMagicSparkles} className="h-4 w-4 text-indigo-400" />
        <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">Key risks</h3>
        {insights?.generated_at && !loading ? (
          <span className="text-xs text-slate-500">{relativeTime(insights.generated_at)}</span>
        ) : null}
      </div>

      {loading ? (
        <p className="flex items-center gap-2 py-4 text-sm text-slate-400">
          <FontAwesomeIcon icon={faSpinner} className="h-4 w-4 animate-spin" />
          Generating management digest…
        </p>
      ) : insights ? (
        <div className="space-y-3">
          {insights.headline ? (
            <p className="text-sm leading-relaxed text-slate-700 dark:text-slate-200">{insights.headline}</p>
          ) : null}
          {insights.key_risks.length > 0 ? (
            <ul className="space-y-2.5">
              {insights.key_risks.map((risk) => (
                <li key={risk.problem_statement_id} className="flex gap-2.5">
                  <span
                    className={clsx("mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full", SEVERITY_DOT[risk.severity])}
                  />
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-slate-700 dark:text-slate-200">
                      <span className="text-slate-500">{risk.problem_statement_id}</span> · {risk.title}
                    </p>
                    <p className="mt-0.5 text-sm leading-relaxed text-slate-400">{risk.insight}</p>
                    {risk.stat ? <p className="mt-1 text-xs text-slate-500">{risk.stat}</p> : null}
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-slate-500">No material risks identified across reviewed cases.</p>
          )}
        </div>
      ) : (
        <p className="text-sm text-slate-500">Management digest unavailable.</p>
      )}
    </div>
  );
}

function StatementScorecardRow({
  row,
  insightText,
}: {
  row: PortfolioProblemStatementRow;
  insightText: string | null;
}) {
  const hasIssue = row.fail_count + row.unclear_count > 0;
  const concentration = concentrationLabel(row.fail_count, row.applicable_count);

  return (
    <li className="rounded-lg px-3 py-2.5 ring-1 ring-slate-200 dark:ring-white/5">
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-sm font-medium text-slate-700 dark:text-slate-200">
              <span className="text-slate-500">{row.problem_statement_id}</span> · {row.title}
            </p>
            {concentration ? (
              <span className="rounded bg-red-500/15 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-red-300 ring-1 ring-red-500/25">
                {concentration}
              </span>
            ) : null}
          </div>
          <RateBar failPct={row.fail_rate_pct} issuePct={row.issue_rate_pct} applicable={row.applicable_count > 0} />
          <p className="mt-1 text-xs tabular-nums text-slate-500">
            {row.fail_count > 0 ? <span className="font-semibold text-red-400">{row.fail_count} No</span> : null}
            {row.fail_count > 0 && row.unclear_count > 0 ? <span> · </span> : null}
            {row.unclear_count > 0 ? (
              <span className="font-semibold text-amber-400">{row.unclear_count} Unclear</span>
            ) : null}
            {hasIssue ? null : <span className="text-emerald-400">{row.pass_count} Yes</span>}
            <span> · {row.applicable_count} applicable</span>
          </p>
          {hasIssue && insightText ? (
            <p className="mt-2 text-sm leading-relaxed text-slate-400">{insightText}</p>
          ) : null}
        </div>
        <div className="shrink-0 text-right">
          <p className="text-sm font-semibold tabular-nums text-slate-700 dark:text-slate-200">{formatRatePct(row.issue_rate_pct)}</p>
          <p className="text-[10px] text-slate-500">issue rate</p>
        </div>
      </div>
    </li>
  );
}

function RateBar({ failPct, issuePct, applicable }: { failPct: number; issuePct: number; applicable: boolean }) {
  if (!applicable) {
    return <div className="mt-1.5 h-1.5 w-full rounded-full bg-slate-100 dark:bg-white/5" />;
  }
  const unclearPct = Math.max(0, issuePct - failPct);
  return (
    <div className="mt-1.5 flex h-1.5 w-full overflow-hidden rounded-full bg-slate-100 dark:bg-white/5">
      {failPct > 0 ? <div className="h-full bg-red-500/70" style={{ width: `${failPct}%` }} /> : null}
      {unclearPct > 0 ? <div className="h-full bg-amber-500/60" style={{ width: `${unclearPct}%` }} /> : null}
    </div>
  );
}

function Header({
  summary,
  onRefresh,
  refreshing,
}: {
  summary: string;
  onRefresh: () => void;
  refreshing: boolean;
}) {
  return (
    <div className="flex items-center justify-between gap-3">
      <div className="min-w-0">
        <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100">Coverage insights</h2>
        <p className="mt-0.5 text-sm text-slate-400">{summary}</p>
      </div>
      <Button
        plain size="unstyled"
        type="button"
        onClick={onRefresh}
        disabled={refreshing}
        className={clsx(CC_GHOST_BTN, "inline-flex shrink-0 py-2 disabled:opacity-50")}
      >
        <FontAwesomeIcon
          icon={refreshing ? faSpinner : faRotate}
          className={clsx("h-3.5 w-3.5", refreshing && "animate-spin")}
        />
        {refreshing ? "Refreshing…" : "Refresh"}
      </Button>
    </div>
  );
}

function Filters({
  adviserFilter,
  caseTypeFilter,
  adviserOptions,
  caseTypeOptions,
  onAdviserChange,
  onCaseTypeChange,
  onClear,
  hasActiveFilter,
}: {
  adviserFilter: string;
  caseTypeFilter: string;
  adviserOptions: string[];
  caseTypeOptions: string[];
  onAdviserChange: (v: string) => void;
  onCaseTypeChange: (v: string) => void;
  onClear: () => void;
  hasActiveFilter: boolean;
}) {
  return (
    <InsightsFilterBar
      adviserFilter={adviserFilter}
      caseTypeFilter={caseTypeFilter}
      adviserOptions={adviserOptions}
      caseTypeOptions={caseTypeOptions}
      formatCaseTypeLabel={(slug) => slug.replace(/-/g, " ")}
      onAdviserChange={onAdviserChange}
      onCaseTypeChange={onCaseTypeChange}
      onClear={onClear}
      hasActiveFilter={hasActiveFilter}
      adviserTestId="ps-insights-filter-adviser"
      caseTypeTestId="ps-insights-filter-case-type"
      clearTestId="ps-insights-filter-clear"
    />
  );
}

function StatChip({
  label,
  value,
  tone,
}: {
  label: string;
  value: number | string;
  tone?: "emerald" | "amber" | "red";
}) {
  const valueTone =
    tone === "emerald"
      ? "text-emerald-400"
      : tone === "amber"
        ? "text-amber-400"
        : tone === "red"
          ? "text-red-400"
          : "text-slate-900 dark:text-slate-100";

  return (
    <div className={clsx(CC_PANEL, "py-3")}>
      <p className={clsx("text-2xl font-semibold tabular-nums", valueTone)}>{value}</p>
      <p className="mt-0.5 text-xs text-slate-400">{label}</p>
    </div>
  );
}
